import * as React from 'react';

import type * as api from '@gitbook/api-client';
import {
    Dialog,
    DialogBody,
    DialogHeader,
    LoadingPanel,
    Stepper,
    StepperStep,
} from '@gitbook/spine-react';

import {
    IntegrationAutoInstall,
    IntegrationConfigureOrgFlowById,
    IntegrationConfigureSpaceFlowById,
    IntegrationDialogAside,
} from '../../Integrations';
import { ConfigureContentIntegrationSourceFlow } from './ConfigureContentIntegrationSourceFlow';
import { PickIntegrationContentSourceFlow } from './PickIntegrationContentSourceFlow';

export interface ConfigureNewContentIntegrationSourceDialogProps {
    /** Organization in which the flow is being done */
    organizationId: string;
    /** Space in which the flow is being done */
    spaceId: string;
    /** Callback when the configuration is done. */
    onSubmit: (source: api.ComputedContentSourceIntegration) => void;
    /** Pre-selected content source */
    selected?: {
        integration: api.Integration;
        contentSource: api.IntegrationContentSource;
    };
}

type FlowStep =
    | {
          step: 'source';
      }
    | {
          step: 'configure_installation';
          integration: api.Integration;
          contentSource: api.IntegrationContentSource;
      }
    | {
          step: 'configure_space_installation';
          integration: api.Integration;
          contentSource: api.IntegrationContentSource;
      }
    | {
          step: 'configure_source';
          integration: api.Integration;
          contentSource: api.IntegrationContentSource;
      };

/**
 * Dialog to configure a new content source.
 */
export function ConfigureNewContentIntegrationSourceDialog(
    props: ConfigureNewContentIntegrationSourceDialogProps
) {
    return (
        <Dialog size={{ width: 896, height: 640 }}>
            <DialogContent {...props} />
        </Dialog>
    );
}

function DialogContent(props: ConfigureNewContentIntegrationSourceDialogProps) {
    const { organizationId, spaceId, onSubmit, selected } = props;

    const initialStep = selected ? getStepForSelected(selected) : { step: 'source' as const };
    const [currentStep, setCurrentStep] = React.useState<FlowStep>(initialStep);

    return (
        <div className="flex max-md:flex-col md:h-full">
            <IntegrationDialogAside
                integration={'integration' in currentStep ? currentStep.integration : undefined}
            />
            <div className="flex flex-1 flex-col md:overflow-auto">
                {initialStep.step !== 'configure_source' ? (
                    <DialogHeader>
                        <Stepper<FlowStep['step']>
                            value={currentStep.step}
                            onChange={(step) => {
                                if (step === 'source') {
                                    setCurrentStep({ step: 'source' });
                                }
                            }}
                        >
                            <StepperStep id="source" title="Select a source" />
                            <StepperStep
                                id="configure_installation"
                                title="Configure the integration"
                            />
                            <StepperStep
                                id="configure_space_installation"
                                title="Configure the space"
                            />
                            <StepperStep id="configure_source" title="Configure the source" />
                        </Stepper>
                    </DialogHeader>
                ) : null}

                <React.Suspense fallback={<LoadingPanel size="large" />}>
                    {currentStep.step === 'source' ? (
                        <PickIntegrationContentSourceFlow
                            organizationId={organizationId}
                            onSubmit={(result) => {
                                setCurrentStep(
                                    getStepForSelected({
                                        integration: result.integration,
                                        contentSource: result.contentSource,
                                    })
                                );
                            }}
                        />
                    ) : null}

                    {currentStep.step === 'configure_installation' ? (
                        <DialogBody>
                            <IntegrationConfigureOrgFlowById
                                integrationName={currentStep.integration.name}
                                organizationId={organizationId}
                                onComplete={() => {
                                    if (currentStep.integration.configurations?.space) {
                                        setCurrentStep({
                                            step: 'configure_space_installation',
                                            integration: currentStep.integration,
                                            contentSource: currentStep.contentSource,
                                        });
                                    } else {
                                        setCurrentStep({
                                            step: 'configure_source',
                                            integration: currentStep.integration,
                                            contentSource: currentStep.contentSource,
                                        });
                                    }
                                }}
                            />
                        </DialogBody>
                    ) : null}

                    {currentStep.step === 'configure_space_installation' ? (
                        <DialogBody>
                            <IntegrationConfigureSpaceFlowById
                                integrationName={currentStep.integration.name}
                                organizationId={organizationId}
                                spaceId={spaceId}
                                onComplete={() => {
                                    setCurrentStep({
                                        step: 'configure_source',
                                        integration: currentStep.integration,
                                        contentSource: currentStep.contentSource,
                                    });
                                }}
                            />
                        </DialogBody>
                    ) : null}

                    {currentStep.step === 'configure_source' ? (
                        <DialogBody>
                            <IntegrationAutoInstall
                                spaceId={spaceId}
                                organizationId={organizationId}
                                integration={currentStep.integration}
                            >
                                <ConfigureContentIntegrationSourceFlow
                                    organizationId={organizationId}
                                    spaceId={spaceId}
                                    integration={currentStep.integration}
                                    contentSource={currentStep.contentSource}
                                    onSubmit={onSubmit}
                                />
                            </IntegrationAutoInstall>
                        </DialogBody>
                    ) : null}
                </React.Suspense>
            </div>
        </div>
    );
}

function getStepForSelected(selected: {
    integration: api.Integration;
    contentSource: api.IntegrationContentSource;
}): FlowStep {
    if (selected.integration.configurations?.account) {
        return {
            step: 'configure_installation',
            integration: selected.integration,
            contentSource: selected.contentSource,
        };
    }

    if (selected.integration.configurations?.space) {
        return {
            step: 'configure_space_installation',
            integration: selected.integration,
            contentSource: selected.contentSource,
        };
    }

    return {
        step: 'configure_source',
        integration: selected.integration,
        contentSource: selected.contentSource,
    };
}
